import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLearning } from 'src/hook/data/useLearning';
import useProgress from 'src/hook/data/useProgress';
import { useSocial } from 'src/hook/data/useSocial';
import useUser from 'src/hook/data/useUser';
import { useWordList } from 'src/hook/data/useWordList';
import { useWords } from 'src/hook/data/useWords';
import { useWordTranslate } from 'src/hook/data/useWordTranslate';
import { useShare } from 'src/hook/global/useShare';
import { useComponentModal } from 'src/hook/modal/useComponentModal';
import { AnimationType, ChevronType, PageSource } from 'src/model/enum/enums';
import { useIFrameModal } from '../../../../hook/modal/useIFrameModal';
import { GetRazorSiteUrl, GetWebSiteUrl, IconLocation } from '../../../../utils/URLUtils';
import { IFrame } from '../../GlobalComponents/IFrame/IFrame';
import { Chevron } from '../Chevron/Chevron';
import Spinner from '../Spinner/Spinner';
import styles from './IFrameModal.module.scss';

const IFrameModal = () => {
  const { isOpen, url, title, close, open } = useIFrameModal();
  const [hasNavigated, setHasNavigated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [zIndex, setZIndex] = useState(9999999);
  const { fetchUserData, userInfo } = useUser();
  const { fetchTranslates } = useWordTranslate(false);
  const navigate = useNavigate();
  const { uploadProgressAtomic } = useProgress();
  const { fetchLearningData, learningInfo } = useLearning();
  const { share } = useShare();
  const { getWordById } = useWords();
  const { fetchSocialInfo } = useSocial(false);
  const { fetchAllWordListData } = useWordList();
  const { open: openComponentModal } = useComponentModal();
  const isChatUrl = url?.includes('practice/fantasy-chat');
  const isFriendUrl = url?.includes('profile/friend') || url?.includes('profile/add-friend');
  const isSettingsUrl = url?.includes('settings/preferences');
  const { t } = useTranslation();

  const updateData = useCallback(async () => {
    const currentLanguageCode = userInfo?.NativeLanguageCode;
    const [user, learning] = await Promise.all([
      fetchUserData(userInfo?.Token!),
      fetchLearningData(userInfo?.Token!),
    ]);
    fetchAllWordListData(learning?.WordLists || []);

    if (isSettingsUrl && currentLanguageCode !== user?.NativeLanguageCode)
      fetchTranslates(user?.NativeLanguageCode);

    if (isFriendUrl)
      fetchSocialInfo(user?.UserId!, user?.CountryCode);
  }, [fetchUserData, fetchTranslates, fetchLearningData,
    fetchSocialInfo, isSettingsUrl, isFriendUrl, userInfo, fetchAllWordListData]);

  const handleClose = useCallback(async () => {
    const iframe = document.querySelector('iframe');
    if (iframe?.contentWindow && hasNavigated) {
      iframe.contentWindow.postMessage({ type: 'NAVIGATE_BACK' }, '*');
    } else {
      setIsClosing(true);
      setTimeout(() => {
        close();
        setIsClosing(false);
      }, 300);
      updateData();
    }
  }, [close, hasNavigated, updateData]);

  const handleOpenFantasyChat = useCallback((url: string) => {
    const urlParams = new URLSearchParams(url.split('?')[1]);
    const personId = urlParams.get('personId');
    const personName = urlParams.get('personName');

    if (personId) {
      open(GetRazorSiteUrl(`practice/fantasy-chat`,
        userInfo?.Token, `&personId=${personId}`),
        personName || '');
    }
  }, [open, userInfo?.Token]);

  const handleOpenWordList = useCallback((url: string) => {
    let title = url.split('/').pop();
    if (!title) return;
    const wordlist = learningInfo?.WordLists?.find(wordlist => wordlist?.Title && title && wordlist.Title.includes(title));
    if (!wordlist) return;
    navigate(`/wordlists/${wordlist.IsCompleted ? 'wordlist' : 'discover'}?id=${wordlist.ID}`);
    handleClose();
  }, [navigate, learningInfo, handleClose]);

  const handleOpenDefinition = useCallback((url: string) => {
    try {
      const wordId = url.split('wordId=')[1];
      if (wordId) {
        setZIndex(999);
        const word = getWordById(parseInt(wordId));
        openComponentModal('Definition', { word, source: PageSource.Chat }, AnimationType.SlideUp);
      }
    } catch (error) {
      console.error('Error parsing definition URL:', error);
    }
  }, [getWordById, openComponentModal]);

  const handleShare = () => {
    share(`${GetWebSiteUrl()}`);
  };

  useEffect(() => {
    const init = async () => {
      await uploadProgressAtomic();
      setIsLoading(false);
    };
    init();

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'NAVIGATION_COMPLETED') {
        setHasNavigated(true);
      } else if (event.data.type === 'NAVIGATION_BACK_COMPLETED') {
        setHasNavigated(false);
      }
      else if (event.data.type === 'NAVIGATION_TO_DOMAIN') {
        var url = event.data.url;
        if (url.startsWith('/')) {
          url = url.slice(1);
        }
        if (url.includes('defintion')) {
          handleOpenDefinition(url);
        }
        else if (url.includes('wordlists') && !url.includes('create-new')) {
          handleOpenWordList(url);
        }
        else if (url.includes('practice/fantasy-chat')) {
          handleOpenFantasyChat(url);
        }
        else {
          console.log(url);
          navigate(`/${url}`);
          handleClose();
        }

      }
    };


    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [close, navigate, uploadProgressAtomic,
    handleClose, handleOpenDefinition, handleOpenWordList, handleOpenFantasyChat]);



  if (!isOpen) return null;
  if (isLoading) return <Spinner />

  return (
    <div className={`${styles.iFrameModal} fullPage ${isClosing ? styles.closing : ''}`} style={{ zIndex }}>
      <div className={styles.iframeHeader}>
        <Chevron type={ChevronType.Left} onClick={handleClose} />
        {!hasNavigated && <h1>{t(title)}</h1>}
        {isChatUrl && (
          <img
            className={styles.icons}
            src={IconLocation('share')}
            alt="share"
            onClick={handleShare}
          />
        )}
      </div>
      <div className={styles.iframeContainer}>
        <IFrame src={url} title={title} />
      </div>
    </div>
  );
};

export default IFrameModal;